/**
 * 81. POS Order/Cart — cart; line items; payments; receipt;
 * touch-optimized checkout.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  Badge,
  Field,
  Input,
} from "@/components/ui";
import { useApiQuery } from "@/lib/api/hooks";
import { formatMoney } from "@/lib/money";

interface PosOrderData {
  sessionNumber: string;
  terminal: string;
  currency: string;
  lines: Array<{
    id: string;
    productSku: string;
    productName: string;
    quantity: number;
    unitPrice: string;
    lineTotal: string;
  }>;
  subtotal: string;
  taxTotal: string;
  total: string;
}

export function PosOrderPage() {
  const { data, isLoading } = useApiQuery<PosOrderData>(
    ["pos", "order"],
    "/pos/sessions/active",
  );
  const [scanInput, setScanInput] = useState("");

  if (isLoading || !data) return <Skeleton rows={8} />;

  return (
    <div>
      <PageHeader
        title={`POS — ${data.sessionNumber}`}
        subtitle={`Terminal ${data.terminal} · ${data.currency}`}
        actions={<Button variant="primary">Charge</Button>}
        breadcrumb={["POS", "Order / Cart"]}
      />

      <div className="atlas-grid atlas-grid--2">
        <Card title="Cart">
          <div className="mb-4">
            <Field label="Scan barcode">
              <Input
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                placeholder="Scan or type SKU…"
                autoFocus
              />
            </Field>
          </div>
          <DataTable
            rows={data.lines}
            keyField="id"
            columns={[
              {
                key: "productSku",
                label: "SKU",
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "productName", label: "Product" },
              { key: "quantity", label: "Qty", numeric: true },
              {
                key: "unitPrice",
                label: "Unit",
                numeric: true,
                render: (r) => formatMoney(r.unitPrice, data.currency),
              },
              {
                key: "lineTotal",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.lineTotal, data.currency),
              },
            ]}
          />
        </Card>
        <Card title="Totals">
          <dl
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
              fontSize: 15,
            }}
          >
            <dt className="atlas-muted">Subtotal</dt>
            <dd className="atlas-mono">
              {formatMoney(data.subtotal, data.currency)}
            </dd>
            <dt className="atlas-muted">Tax</dt>
            <dd className="atlas-mono">
              {formatMoney(data.taxTotal, data.currency)}
            </dd>
            <dt className="atlas-muted">Total</dt>
            <dd
              className="atlas-mono"
              style={{ fontWeight: 700, fontSize: 20 }}
            >
              {formatMoney(data.total, data.currency)}
            </dd>
          </dl>
          <div
            className="mt-4"
            style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
          >
            <Button variant="primary">Cash</Button>
            <Button>Card</Button>
            <Button>Mobile</Button>
            <Badge tone="warning">Guest customer</Badge>
          </div>
        </Card>
      </div>
    </div>
  );
}
